import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { DOCS_HELP_CENTER_HREF } from "../../lib/supportLinks";
import {
  BookOpen,
  BookText,
  ChevronRight,
  ClipboardCheck,
  FileSearch,
  FlaskConical,
  LayoutTemplate,
  Library,
  ListChecks,
  Logs,
  Plus,
  ShieldCheck,
} from "lucide-react";
import { SidebarLink } from "./sidebarLink";
import { useAuth } from "../../contexts/authContext";
import { BackgroundTasksPopover } from "./BackgroundTasksPopover";
import { useFieldGroupsContext } from "../../contexts/fieldGroupsContext";
import { cn } from "../../lib/utils";
import { useSoc1ExtractionWorkpaperEnabled } from "../../hooks/useSoc1ExtractionWorkpaperEnabled";
import { hasPermission } from "../../lib/permissions";
import { useWorkStepsEnabled } from "../../hooks/useWorkStepsEnabled";

interface AuditSidebarProps {
  isCollapsed: boolean;
  // Reports the Background Tasks popover's open state up to the sidebar
  // container, which keeps the sidebar expanded for as long as the popover
  // is open (see sidebarContainer.tsx for why this is necessary).
  onBackgroundTasksOpenChange: (isOpen: boolean) => void;
}

export const AuditSidebar = ({
  isCollapsed,
  onBackgroundTasksOpenChange,
}: AuditSidebarProps) => {
  const { workspaceMember } = useAuth();
  const { fieldGroups } = useFieldGroupsContext();
  const [isLibrariesExpanded, setIsLibrariesExpanded] = useState(true);
  const { pathname: currentPath } = useLocation();
  const reportExtractionsEnabled = useSoc1ExtractionWorkpaperEnabled();
  const workStepsEnabled = useWorkStepsEnabled();

  return (
    <>
      {/* Fieldwork section: the testable entities — Work Steps and Controls,
          nested under Fieldwork, which is itself the unified controls + work
          steps testing list at /audit/fieldwork.
          (Distinct from the legacy Fieldwork tab on the Controls page.)
          Gated on the work-steps flag: when off, the Fieldwork group
          disappears entirely — its only flag-gated entry (Work Steps) goes
          with it — and Controls renders as the ungrouped top-level link it
          was before Work Steps existed. */}
      {workStepsEnabled ? (
        <>
          <SidebarLink
            path="/audit/fieldwork"
            title="Fieldwork"
            icon={<FlaskConical size={20} />}
            isCollapsed={isCollapsed}
          />
          {/* There is only one Controls page. The old !controlsV2Enabled
              branch pointed at /audit/controls, which is not in the route
              table, so it fell through to the "*" wildcard -> /audit and
              remounted the whole /audit layout (dropping in-flight
              import/provider state and the websocket connection) before
              the index redirect landed here anyway. */}
          {/* The indent only makes sense beside a visible parent label. While
              collapsed there is no label and no hierarchy to show, so it would
              just push these two icons out of line with every other row. */}
          <div className={cn(isCollapsed ? "" : "pl-4")}>
            <SidebarLink
              path="/audit/controls/v2"
              title="Controls"
              icon={<ShieldCheck size={20} />}
              isCollapsed={isCollapsed}
            />
          </div>
          <div className={cn(isCollapsed ? "" : "pl-4")}>
            <SidebarLink
              path="/audit/work-steps"
              title="Work Steps"
              icon={<ListChecks size={20} />}
              isCollapsed={isCollapsed}
            />
          </div>
        </>
      ) : (
        <SidebarLink
          path="/audit/controls/v2"
          title="Controls"
          icon={<ShieldCheck size={20} />}
          isCollapsed={isCollapsed}
        />
      )}
      <SidebarLink
        path="/audit/work-paper-templates"
        title="Templates"
        icon={<LayoutTemplate size={20} />}
        isCollapsed={isCollapsed}
      />
      <SidebarLink
        path="/audit/documents"
        title="Workspace Docs"
        icon={<BookOpen size={20} />}
        isCollapsed={isCollapsed}
      />

      {/* Libraries Section */}
      <div className="flex flex-col">
        <button
          onClick={() => setIsLibrariesExpanded(!isLibrariesExpanded)}
          className={cn(
            "flex items-center p-3 text-sm hover:bg-stone-50 transition-colors duration-200 border border-transparent w-full",
            isCollapsed ? "justify-center" : "",
          )}
        >
          <div
            className={cn(
              "flex items-center min-w-0 flex-1",
              isCollapsed ? "justify-center gap-0" : "gap-2",
            )}
          >
            <div className="flex-shrink-0">
              <Library size={20} />
            </div>
            <span
              className={cn(
                "truncate whitespace-nowrap transition-all duration-300",
                isCollapsed
                  ? "w-0 opacity-0 overflow-hidden"
                  : "w-auto opacity-100",
              )}
            >
              Libraries
            </span>
          </div>
          {!isCollapsed && (
            <ChevronRight
              size={16}
              className={cn(
                "transition-transform duration-200 text-stone-400",
                isLibrariesExpanded ? "rotate-90" : "",
              )}
            />
          )}
        </button>

        {/* Expandable library links */}
        {!isCollapsed && isLibrariesExpanded && (
          <div className="flex flex-col">
            {fieldGroups.map((group) => (
              <Link
                key={group.id}
                to={`/audit/libraries/${group.id}`}
                className={cn(
                  "flex items-center pl-10 pr-3 py-2 text-sm hover:bg-stone-50 transition-colors duration-200 border border-transparent",
                  currentPath === `/audit/libraries/${group.id}`
                    ? "bg-stone-50 border-stone-200"
                    : "",
                )}
              >
                <span className="truncate">{group.name}</span>
              </Link>
            ))}
            <Link
              to="/audit/libraries/new"
              className="flex items-center gap-1 pl-10 pr-3 py-2 text-sm text-stone-500 hover:text-stone-700 hover:bg-stone-50 transition-colors duration-200"
            >
              <Plus size={14} />
              <span>New</span>
            </Link>
          </div>
        )}
      </div>

      {reportExtractionsEnabled && (
        <SidebarLink
          path="/audit/report-extraction/jobs"
          title="Report Extractions"
          icon={<FileSearch size={20} />}
          isCollapsed={isCollapsed}
        />
      )}

      {hasPermission(workspaceMember, "manage_approvals") && (
        <SidebarLink
          path="/audit/approvals"
          title="Approvals"
          icon={<ClipboardCheck size={20} />}
          isCollapsed={isCollapsed}
        />
      )}
      {hasPermission(workspaceMember, "view_audit_logs") && (
        <SidebarLink
          path="/audit/audit-logs"
          title="Audit Logs"
          icon={<Logs size={20} />}
          isCollapsed={isCollapsed}
        />
      )}
      <SidebarLink
        href={DOCS_HELP_CENTER_HREF}
        title="Docs"
        icon={<BookText size={20} />}
        external
        isCollapsed={isCollapsed}
      />
      <BackgroundTasksPopover
        isCollapsed={isCollapsed}
        onOpenChange={onBackgroundTasksOpenChange}
      />
    </>
  );
};
